import { useState } from 'react';
import { useParams, Link } from 'react-router';
import {
  Trophy,
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  Clock,
  Target,
  BarChart3,
  AlertTriangle,
  CheckCircle,
  ShieldAlert,
  XCircle,
} from 'lucide-react';
import { useChallenge, useChallengeSnapshots, useCancelChallenge } from '@/hooks/use-challenges';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';

export default function ChallengeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: challenge, isLoading } = useChallenge(id!);
  const { data: snapshots } = useChallengeSnapshots(id!);
  const cancelChallenge = useCancelChallenge();
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  if (isLoading || !challenge) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  const metrics = challenge.metrics;
  const isActive = challenge.status === 'ACTIVE';
  const isPending = challenge.status === 'PENDING';
  const isPassed = challenge.status === 'PASSED';
  const isFailed = challenge.status === 'FAILED' || challenge.status === 'EXPIRED';

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      {/* Back link */}
      <Link to="/challenges" className="inline-flex items-center gap-1.5 text-xs text-ink-3 hover:text-ink transition-colors">
        <ArrowLeft size={14} />
        All Challenges
      </Link>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Trophy size={24} className="text-orange" />
          <div>
            <h1 className="text-xl font-extrabold tracking-tight">
              {challenge.tier} Challenge
            </h1>
            <p className="text-xs text-ink-3">
              {challenge.bot?.symbol || 'Pending'} · ${challenge.starting_capital.toLocaleString()} capital
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge color={isPassed ? 'green' : isFailed ? 'red' : isActive ? 'blue' : 'default'}>
            {isPassed && <CheckCircle size={14} className="mr-1" />}
            {isFailed && <AlertTriangle size={14} className="mr-1" />}
            {challenge.status}
          </Badge>
          {(isActive || isPending) && (
            showCancelConfirm ? (
              <div className="flex items-center gap-1.5">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowCancelConfirm(false)}
                >
                  Keep
                </Button>
                <Button
                  size="sm"
                  onClick={() => cancelChallenge.mutate(challenge.id)}
                  disabled={cancelChallenge.isPending}
                  className="!bg-red !text-white"
                >
                  <XCircle size={12} className="mr-1" />
                  {cancelChallenge.isPending ? 'Cancelling...' : 'Confirm Cancel'}
                </Button>
              </div>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowCancelConfirm(true)}
              >
                <XCircle size={12} className="mr-1" />
                Cancel
              </Button>
            )
          )}
        </div>
      </div>

      {/* Progress bar (active only) */}
      {isActive && metrics && (
        <div className="glass rounded-xl p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-bold">Progress to Target</span>
            <span className="text-sm font-extrabold text-orange">
              {metrics.progress_to_target_pct.toFixed(1)}%
            </span>
          </div>
          <div className="w-full h-2.5 bg-surface rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{
                width: `${Math.min(100, Math.max(0, metrics.progress_to_target_pct))}%`,
                background: 'linear-gradient(90deg, #f97316, #f59e0b)',
              }}
            />
          </div>
          <div className="flex items-center justify-between mt-2 text-[11px] text-ink-4">
            <span>$0</span>
            <span>${(challenge.starting_capital * (1 + challenge.profit_target_pct / 100)).toLocaleString()} target</span>
          </div>
        </div>
      )}

      {/* Live metrics */}
      {metrics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricCard
            icon={TrendingUp}
            label="Current Equity"
            value={`$${metrics.current_equity.toLocaleString()}`}
            color={metrics.return_pct >= 0 ? 'text-green' : 'text-red'}
          />
          <MetricCard
            icon={BarChart3}
            label="Return"
            value={`${metrics.return_pct >= 0 ? '+' : ''}${metrics.return_pct.toFixed(2)}%`}
            color={metrics.return_pct >= 0 ? 'text-green' : 'text-red'}
          />
          <MetricCard
            icon={TrendingDown}
            label="Drawdown"
            value={`${metrics.drawdown_pct.toFixed(2)}%`}
            sublabel={`Max ${challenge.max_drawdown_pct}%`}
            color={metrics.drawdown_pct > challenge.max_drawdown_pct * 0.7 ? 'text-red' : 'text-ink'}
          />
          <MetricCard
            icon={ShieldAlert}
            label="Daily Loss"
            value={`${metrics.daily_loss_pct.toFixed(2)}%`}
            sublabel={`Max ${challenge.max_daily_loss_pct}%`}
            color={
              metrics.daily_loss_pct > challenge.max_daily_loss_pct * 0.7
                ? 'text-red'
                : metrics.daily_loss_pct > 0
                  ? 'text-amber'
                  : 'text-ink'
            }
          />
          <MetricCard
            icon={Clock}
            label="Days Remaining"
            value={`${metrics.days_remaining}`}
            sublabel={`of ${challenge.duration_days}`}
          />
          <MetricCard
            icon={Target}
            label="Profit Target"
            value={`${challenge.profit_target_pct}%`}
            sublabel={`$${(challenge.starting_capital * challenge.profit_target_pct / 100).toLocaleString()} profit`}
          />
          <MetricCard
            icon={Trophy}
            label="Reward"
            value={`$${challenge.reward_amount}`}
            color="text-orange"
          />
        </div>
      )}

      {/* Challenge rules */}
      <div className="glass rounded-xl p-5">
        <h2 className="text-sm font-bold mb-3">Challenge Rules</h2>
        <div className="grid grid-cols-2 gap-3 text-xs">
          <RuleRow label="Profit Target" value={`${challenge.profit_target_pct}%`} />
          <RuleRow label="Max Drawdown" value={`${challenge.max_drawdown_pct}%`} />
          <RuleRow label="Max Daily Loss" value={`${challenge.max_daily_loss_pct}%`} />
          <RuleRow label="Min Trades" value={`${challenge.min_trades}`} current={challenge.bot?.total_trades} />
          <RuleRow label="Min Profit Factor" value={`${challenge.min_profit_factor}`} />
          <RuleRow label="Duration" value={`${challenge.duration_days} days`} />
        </div>
      </div>

      {/* Bot link */}
      {challenge.bot && (
        <div className="glass rounded-xl p-5">
          <h2 className="text-sm font-bold mb-3">Challenge Bot</h2>
          <Link
            to={`/bots/${challenge.bot.id}`}
            className="flex items-center justify-between p-3 rounded-lg bg-surface hover:bg-surface/80 transition-colors"
          >
            <div>
              <div className="text-sm font-bold">{challenge.bot.name}</div>
              <div className="text-xs text-ink-3 mt-0.5">
                {challenge.bot.total_trades} trades · P&L: ${challenge.bot.total_pnl?.toFixed(2)}
              </div>
            </div>
            <Badge color={challenge.bot.is_active ? 'green' : 'default'}>
              {challenge.bot.is_active ? 'Running' : 'Stopped'}
            </Badge>
          </Link>
        </div>
      )}

      {/* Daily snapshots */}
      {snapshots && snapshots.length > 0 && (
        <div className="glass rounded-xl p-5">
          <h2 className="text-sm font-bold mb-3">Daily Performance</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-ink-4 text-left">
                  <th className="pb-2 font-medium">Date</th>
                  <th className="pb-2 font-medium text-right">Start Equity</th>
                  <th className="pb-2 font-medium text-right">End Equity</th>
                  <th className="pb-2 font-medium text-right">Daily P&L</th>
                  <th className="pb-2 font-medium text-right">Trades</th>
                </tr>
              </thead>
              <tbody>
                {snapshots.slice().reverse().map((snap) => (
                  <tr key={snap.id} className="border-t border-border">
                    <td className="py-2 font-medium">{snap.date}</td>
                    <td className="py-2 text-right">${snap.start_of_day_equity.toFixed(0)}</td>
                    <td className="py-2 text-right">
                      {snap.end_of_day_equity ? `$${snap.end_of_day_equity.toFixed(0)}` : '—'}
                    </td>
                    <td className={`py-2 text-right font-bold ${snap.daily_pnl >= 0 ? 'text-green' : 'text-red'}`}>
                      {snap.daily_pnl >= 0 ? '+' : ''}{snap.daily_pnl.toFixed(2)} ({snap.daily_pnl_pct.toFixed(1)}%)
                    </td>
                    <td className="py-2 text-right">{snap.trades_closed}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Failure / Cancelled reason */}
      {isFailed && challenge.failure_reason && (
        <div className="rounded-xl p-5" style={{
          background: challenge.failure_reason === 'CANCELLED' ? 'rgba(100,100,100,0.06)' : 'rgba(239,68,68,0.06)',
          border: challenge.failure_reason === 'CANCELLED' ? '1px solid rgba(100,100,100,0.2)' : '1px solid rgba(239,68,68,0.2)',
        }}>
          <div className="flex items-center gap-2 mb-2">
            {challenge.failure_reason === 'CANCELLED' ? (
              <>
                <XCircle size={16} className="text-ink-3" />
                <span className="text-sm font-bold text-ink-3">Challenge Cancelled</span>
              </>
            ) : (
              <>
                <AlertTriangle size={16} className="text-red" />
                <span className="text-sm font-bold text-red">Challenge Failed</span>
              </>
            )}
          </div>
          <p className="text-xs text-ink-3">
            {challenge.failure_reason !== 'CANCELLED' && <>Reason: {challenge.failure_reason.replace(/_/g, ' ')}</>}
            {challenge.completed_at && (
              <>{challenge.failure_reason !== 'CANCELLED' ? ' · ' : ''}{new Date(challenge.completed_at).toLocaleDateString()}</>
            )}
          </p>
        </div>
      )}

      {/* Pass celebration */}
      {isPassed && (
        <div className="rounded-xl p-5" style={{ background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.2)' }}>
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle size={16} className="text-green" />
            <span className="text-sm font-bold text-green">Challenge Passed!</span>
          </div>
          <p className="text-xs text-ink-3">
            Congratulations! Your reward of ${challenge.reward_amount} is {challenge.reward_status === 'PAID' ? 'paid' : 'pending'}.
          </p>
        </div>
      )}
    </div>
  );
}

function MetricCard({
  icon: Icon,
  label,
  value,
  sublabel,
  color = 'text-ink',
}: {
  icon: typeof TrendingUp;
  label: string;
  value: string;
  sublabel?: string;
  color?: string;
}) {
  return (
    <div className="glass rounded-xl p-4">
      <div className="flex items-center gap-1.5 text-ink-4 mb-1.5">
        <Icon size={13} />
        <span className="text-[10px] uppercase tracking-wider font-medium">{label}</span>
      </div>
      <div className={`text-base font-extrabold ${color}`}>{value}</div>
      {sublabel && <div className="text-[10px] text-ink-4 mt-0.5">{sublabel}</div>}
    </div>
  );
}

function RuleRow({
  label,
  value,
  current,
}: {
  label: string;
  value: string;
  current?: number;
}) {
  return (
    <div className="flex items-center justify-between p-2 rounded-lg bg-surface">
      <span className="text-ink-3">{label}</span>
      <div className="flex items-center gap-2">
        {current !== undefined && (
          <span className="text-ink-4">{current} /</span>
        )}
        <span className="font-bold">{value}</span>
      </div>
    </div>
  );
}
